import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { ShareLinkService } from '../share-link-service';
import {
    CreatePublicShareLinkApi,
} from '@telecom/shared/infrastructure/api-functions/create-public-share-link-api';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { isInIframe } from '@telecom/shared/utils/in-iframe';
import { getNormalizedNestedReportUrl } from '../share-page-route';
import {
    REPORT_SHARE_CHANNEL_SOURCE,
    type ShareLinkAttribution,
} from '../report-share-attribution';

/**
 * Implementation of ShareLinkService
 */
@injectable()
export class ShareLinkServiceImpl implements ShareLinkService {
    constructor(
        @inject(CreatePublicShareLinkApi)
        private _createPublicShareLinkApi: CreatePublicShareLinkApi,
        @inject(CurrentTaskService)
        private _currentTaskService: CurrentTaskService,
    ) {}

    private get _taskId() {
        return this._currentTaskService.taskId;
    }

    /**
     * Get the appropriate window object (handles iframe scenario)
     */
    private _getTargetWindow(): Window {
        return isInIframe() ? window.parent : window;
    }

    /**
     * Build share URL with hash-based routing
     *
     * @param baseUrl - Base URL without hash
     * @param hash - Hash part of the URL
     * @param shareId - Share ID to include in the URL
     * @param attribution - Optional report share registration attribution
     */
    private _buildHashBasedUrl(
        baseUrl: string,
        hash: string,
        shareId: string,
        attribution?: ShareLinkAttribution,
    ): string {
        const hashParts = hash.split('?');
        const hashPath = hashParts[0]; // e.g., '#/dashboard'
        const existingParams = hashParts[1]; // e.g., 'tab=overview&filter=active'

        const params = new URLSearchParams(existingParams);
        params.delete('taskId');
        params.set('task_id', `sh_${shareId}`);
        if (attribution) {
            params.set('channel_source', REPORT_SHARE_CHANNEL_SOURCE);
            params.set('media_type', attribution.mediaType);
            params.set('inviter_id', attribution.inviterId);
            params.set('invitation_code', attribution.invitationCode);
        }
        return `${baseUrl}${hashPath}?${params.toString()}`;
    }

    /**
     * Build share URL with path-based routing
     *
     * @param location - Window location object
     * @param shareId - Share ID to replace the task ID in the path
     * @param attribution - Optional report share registration attribution
     */
    private _buildPathBasedUrl(
        location: Location,
        shareId: string,
        attribution?: ShareLinkAttribution,
    ): string {
        const pathSegments = location.pathname.split('/');
        // Replace the last path segment (task ID) with share ID
        pathSegments[pathSegments.length - 1] = `sh_${shareId}`;
        const newPathname = pathSegments.join('/');
        if (!attribution) {
            return `${location.origin}${newPathname}${location.search}`;
        }

        const params = new URLSearchParams(location.search);
        params.set('channel_source', REPORT_SHARE_CHANNEL_SOURCE);
        params.set('media_type', attribution.mediaType);
        params.set('inviter_id', attribution.inviterId);
        params.set('invitation_code', attribution.invitationCode);
        return `${location.origin}${newPathname}?${params.toString()}`;
    }

    /**
     * Create a share link for a given task
     *
     * @param password - Optional password for protected sharing
     * @returns Promise resolving to the complete share URL
     * @throws Error if the share link creation fails
     */
    async createShareLink(password?: string): Promise<string> {
        try {
            const response = await this._createPublicShareLinkApi.createPublicShareLink({
                body: {
                    task_id: this._taskId,
                    password: password,
                },
            });

            return this.buildShareUrl(response.share_id);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            throw new Error(`Failed to create share link for task ${this._taskId}: ${errorMessage}`);
        }
    }

    buildShareUrl(shareId: string, attribution?: ShareLinkAttribution): string {
        const $window = this._getTargetWindow();
        const { origin, pathname, search, hash } = $window.location;
        const normalizedReportUrl = getNormalizedNestedReportUrl($window.location);

        if (normalizedReportUrl) {
            const normalizedLocation = new URL(normalizedReportUrl);
            return this._buildHashBasedUrl(
                normalizedLocation.origin + normalizedLocation.pathname + normalizedLocation.search,
                normalizedLocation.hash,
                shareId,
                attribution,
            );
        }

        if (hash) {
            return this._buildHashBasedUrl(origin + pathname + search, hash, shareId, attribution);
        }

        return this._buildPathBasedUrl($window.location, shareId, attribution);
    }

    buildForwardShareUrl(): string {
        const $window = this._getTargetWindow();
        const { origin, pathname, search, hash } = $window.location;
        const currentUrl = getNormalizedNestedReportUrl($window.location)
            || `${origin}${pathname}${search}${hash}`;
        const url = new URL(currentUrl);

        if (url.hash) {
            return this._buildForwardHashUrl(url, currentUrl);
        }

        if (!this._hasCompleteShareAttribution(url.searchParams)) {
            return currentUrl;
        }

        url.searchParams.set('media_type', 'link');
        return url.toString();
    }

    private _buildForwardHashUrl(url: URL, currentUrl: string): string {
        const queryStart = url.hash.indexOf('?');
        if (queryStart < 0) {
            return currentUrl;
        }

        const hashPath = url.hash.slice(0, queryStart);
        const params = new URLSearchParams(url.hash.slice(queryStart + 1));
        if (!this._hasCompleteShareAttribution(params)) {
            return currentUrl;
        }

        params.set('media_type', 'link');
        return `${url.origin}${url.pathname}${url.search}${hashPath}?${params.toString()}`;
    }

    private _hasCompleteShareAttribution(params: URLSearchParams): boolean {
        return params.get('channel_source') === REPORT_SHARE_CHANNEL_SOURCE
            && !!params.get('inviter_id')
            && !!params.get('invitation_code');
    }
}
